/**
 * ActivitiesTable — Master list of all activities across all fields.
 * Implements 7W columns: Was | Wer | Wann | Wo | Produkt | Menge | N/P/K
 *
 * Accessible from: Mein Betrieb → Letzte Maßnahmen kachel
 *
 * See: docs/architecture/BUSINESS_EVENT_MODEL.md (7W model)
 */
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import TableView from './TableView';
import { UnifiedStatusBadge } from './unified';

export const TYPE_LABEL_KEYS = {
  FERTILIZATION: 'activity.typeFertilization',
  CROP_PROTECTION: 'activity.typeCropProtection',
  TILLAGE: 'activity.typeTillage',
  SOWING: 'activity.typeSowing',
  HARVEST: 'activity.typeHarvest',
  OTHER: 'activity.typeOther',
};

export const TYPE_ICONS = {
  FERTILIZATION: '🌿',
  CROP_PROTECTION: '💧',
  TILLAGE: '🚜',
  SOWING: '🌱',
  HARVEST: '🌾',
  OTHER: '📋',
};

export function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function fmtNPK(n, p, k) {
  const parts = [];
  if (n != null && n > 0) parts.push(`N ${n.toFixed(0)}`);
  if (p != null && p > 0) parts.push(`P ${p.toFixed(0)}`);
  if (k != null && k > 0) parts.push(`K ${k.toFixed(0)}`);
  return parts.join(' / ') || '—';
}

function getColumns(t) {
  return [
    {
      key: 'status',
      label: t('activity.colStatus'),
      render: row => <UnifiedStatusBadge status={row.status || 'APPROVED'} />,
      sortable: false,
    },
    {
      key: 'activity_type',
      label: t('activity.colWhat'),
      render: row => (
        <span className="flex items-center gap-1.5">
          <span>{TYPE_ICONS[row.activity_type] || '📋'}</span>
          <span>{t(TYPE_LABEL_KEYS[row.activity_type] || 'activity.typeOther')}</span>
        </span>
      ),
      sortable: true,
    },
    {
      key: 'operator',
      label: t('activity.colWho'),
      render: row => row.operator || '—',
      sortable: true,
    },
    {
      key: 'activity_date',
      label: t('activity.colWhen'),
      render: row => fmtDate(row.activity_date),
      sortable: true,
    },
    {
      key: 'field_name',
      label: t('activity.colWhere'),
      render: row => row.field_name || '—',
      sortable: true,
    },
    {
      key: 'product_summary',
      label: t('activity.colProduct'),
      render: row => row.product_summary || '—',
    },
    {
      key: 'quantity_summary',
      label: t('activity.colQuantity'),
      render: row => row.quantity_summary || '—',
    },
    {
      key: 'npk',
      label: t('activity.colNpk'),
      render: row => (
        <span className="text-xs text-[var(--aif-text-muted)]">
          {fmtNPK(row.n_kg_ha, row.p_kg_ha, row.k_kg_ha)}
        </span>
      ),
    },
  ];
}

function getFilterDefs(t) {
  const pastDate = (days) => {
    const d = new Date(); d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };
  return [
    {
      key: 'status',
      options: [
        { value: '__all__', label: t('activity.filterAll') },
        { value: 'APPROVED', label: t('activity.filterOpen') },
        { value: 'POSTED', label: t('activity.filterPosted') },
        { value: 'ARCHIVED', label: t('activity.filterArchived') },
      ],
    },
    {
      key: 'activity_type',
      options: [
        { value: '__all__', label: t('activity.filterAllTypes') },
        { value: 'FERTILIZATION', label: t('activity.typeFertilization') },
        { value: 'CROP_PROTECTION', label: t('activity.typeCropProtection') },
        { value: 'TILLAGE', label: t('activity.typeTillage') },
        { value: 'SOWING', label: t('activity.typeSowing') },
        { value: 'HARVEST', label: t('activity.typeHarvest') },
      ],
    },
    {
      key: '_zeitraum',
      options: [
        { value: '__all__', label: t('activity.filterAllTime') },
        { value: 'week', label: t('activity.filterLastWeek'), filterFn: row => !!(row.activity_date && row.activity_date >= pastDate(7)) },
        { value: 'month', label: t('activity.filterLastMonth'), filterFn: row => !!(row.activity_date && row.activity_date >= pastDate(30)) },
        { value: 'year', label: t('activity.filterLastYear'), filterFn: row => !!(row.activity_date && row.activity_date >= pastDate(365)) },
      ],
    },
  ];
}

export function ActivityDetailSheet({ activity, onClose }) {
  const { t } = useTranslation();
  if (!activity) return null;

  return (
    <div className="fixed inset-0 z-[var(--aif-z-detail-panel)] flex items-end" data-testid="activity-detail-sheet">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-h-[80vh] bg-[var(--aif-bg-page)] rounded-t-2xl shadow-2xl flex flex-col overflow-y-auto">
        <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
          <div className="w-8 h-1 rounded-full bg-[var(--aif-border-default)]" />
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xl">{TYPE_ICONS[activity.activity_type] || '📋'}</span>
                <h3 className="font-semibold text-[var(--aif-text-primary)]">
                  {t(TYPE_LABEL_KEYS[activity.activity_type] || 'activity.typeOther')}
                </h3>
              </div>
              <p className="text-sm text-[var(--aif-text-muted)] mt-0.5">
                {activity.field_name || '—'} · {fmtDate(activity.activity_date)}
              </p>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-[var(--aif-text-muted)]">✕</button>
          </div>

          <div className="grid grid-cols-2 gap-2 text-sm">
            {activity.operator && (
              <div><div className="text-xs text-[var(--aif-text-muted)]">{t('activity.operatorLabel')}</div><div>{activity.operator}</div></div>
            )}
            {activity.area_ha && (
              <div><div className="text-xs text-[var(--aif-text-muted)]">{t('activity.areaLabel')}</div><div>{activity.area_ha} ha</div></div>
            )}
            {activity.machine && (
              <div><div className="text-xs text-[var(--aif-text-muted)]">{t('activity.machineLabel')}</div><div>{activity.machine}</div></div>
            )}
            {activity.cost_total_eur != null && (
              <div>
                <div className="text-xs text-[var(--aif-text-muted)]">{t('activity.costLabel')}</div>
                <div>{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(activity.cost_total_eur)}</div>
              </div>
            )}
          </div>

          {activity.products?.length > 0 && (
            <div>
              <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('activity.productsLabel')}</div>
              {activity.products.map((p, i) => (
                <div key={i} className="text-sm flex justify-between py-1 border-b border-[var(--aif-border-default)] last:border-0">
                  <span>{p.name}</span>
                  <span className="text-[var(--aif-text-muted)]">{p.rate_per_ha} {p.rate_unit}/ha</span>
                </div>
              ))}
            </div>
          )}

          {activity.notes && (
            <div>
              <div className="text-xs text-[var(--aif-text-muted)] mb-1">{t('activity.notesLabel')}</div>
              <p className="text-sm text-[var(--aif-text-primary)]">{activity.notes}</p>
            </div>
          )}

          <UnifiedStatusBadge status={activity.status || 'APPROVED'} />
        </div>
      </div>
    </div>
  );
}

export default function ActivitiesTable({ token }) {
  const { t } = useTranslation();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await fetch('/api/v1/aif/farm/activities?limit=200&sort=date&order=desc', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setActivities(d.items || d.activities || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="activities-table">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--aif-text-primary)]">
          {t('activity.allActivities')}
        </h3>
        <span className="text-xs text-[var(--aif-text-muted)]">{activities.length} {t('ui.total')}</span>
      </div>

      <TableView
        columns={getColumns(t)}
        data={activities}
        searchKeys={['field_name', 'operator', 'product_summary', 'notes']}
        defaultSort={{ key: 'activity_date', direction: 'desc' }}
        filterDefs={getFilterDefs(t)}
        emptyState={{
          title: t('activity.emptyTitle'),
          subtitle: t('activity.emptySubtitle'),
        }}
        onRowClick={row => setSelected(row)}
        mobileCardRenderer={row => (
          <div className="bg-[var(--aif-bg-surface)] rounded-xl px-3 py-2.5 border border-[var(--aif-border-default)]">
            <div className="flex items-center justify-between mb-1">
              <span className="flex items-center gap-1.5 text-sm font-medium text-[var(--aif-text-primary)]">
                <span>{TYPE_ICONS[row.activity_type] || '📋'}</span>
                {t(TYPE_LABEL_KEYS[row.activity_type] || 'activity.typeOther')}
              </span>
              <span className="text-xs text-[var(--aif-text-muted)]">{fmtDate(row.activity_date)}</span>
            </div>
            <div className="text-xs text-[var(--aif-text-muted)]">
              {row.field_name || '—'} · {row.product_summary || '—'}
            </div>
          </div>
        )}
        loading={loading}
      />

      {selected && (
        <ActivityDetailSheet activity={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}
